import {createStore} from 'vuex'
import VuexPersistence from 'vuex-persist'
import SecureLS from 'secure-ls'
import iStoreLocalData from '@/scripts/istore-local-data'
import iStoreSetting from '@/scripts/istore-setting'
import iStoreUtils from '@/scripts/istore-utils'
import iStoreApi from '@/scripts/istore-api';
import {langControl} from '@/scripts/istore-ui-lang';

const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: true,
  storage: window.sessionStorage
}) // Initialize secure storage instance

const secureStorage = {
  setItem: (key, value) => {
    ls.set(key, value)
  },
  getItem: (key) => {
    try {
      return ls.get(key)
    } catch (e) {
      return null
    }
  },
  removeItem: (key) => {
    ls.remove(key)
  }
} // Mimics the secured version of sessionStorage

const vuexLocal = new VuexPersistence({
  key: "vuex_store",
  storage: secureStorage
}) // Define for persistence plugin

export default createStore({
  state: {
    sidebarOver: false,
    sidebarVisible: true,
    sidebarUnfoldable: false,
    sidebarOpenedFolders: [], // Keep track which folder opened
    currentMenu: null,
    iStoreInterfaceData: [], // Store the components data (to generate the dynamic UI) after API call to iStore
    menuFunctions: {},
    iStoreInterfaceDataUpdateTime: 0,
    loginUser: {},
    databases: [],
    activityTimeout: null, // Indicate activity timeout
    notificationMsg: {
      content: null,
      type: null, // Type: warning, normal
      updateTime: 0,
    },
    isValidUrl: true,
    loginTimestamp: 0,
    keepAliveTimestamp: 0,
    logoutClicked: false,
    isLogOutBypassed: false,
    customView: null,
    customData: null,
    checkedItems: [], // To control data table check box
    isTriggerAppMenu: false,
    tempOpenFolder: null,
    globalSettingData: {},
    watermarkSetting: {
      backgroundImage: null
    }
  },
  mutations: {
    setSidebarOpenedFolders(state, openedFolderIds) {
      state.sidebarOpenedFolders = openedFolderIds
    },
    setChosenUrlValidity(state, data) {
      state.isValidUrl = data
    },
    toggleSidebar(state) {
      state.sidebarVisible = !state.sidebarVisible
    },
    toggleUnfoldable(state) {
      state.sidebarUnfoldable = !state.sidebarUnfoldable
    },
    updateSidebarVisible(state, payload) {
      state.sidebarVisible = payload.value
    },
    setCurrentMenu(state, func) {
      if (func) {
        state.currentMenu = func
      } else {
        state.currentMenu = null
      }
    },
    addIStoreInterfaceData(state, data) {
      state.iStoreInterfaceData = data
      state.iStoreInterfaceDataUpdateTime = Date.now()
    },
    // Update iStoreInterfaceData (adding a single node)
    addNode(state, data) {
      if (data && state.loginUser.userFunctions) {
        // state.loginUser.userFunctions.push(data)
        // state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
        // state.iStoreInterfaceDataUpdateTime = Date.now()

        state.loginUser.userFunctions.push(data)

        // Push the same reference (no full clone)
        state.iStoreInterfaceData.push(data)

        state.iStoreInterfaceDataUpdateTime = Date.now()
      }
    },
    // Update iStoreInterfaceData (adding several nodes)
    addNodes(state, data) {
      // if (data) {
      //   console.log('addNodes', data)
      //   data.forEach(d => {
      //     state.loginUser.userFunctions.push(d)
      //   })
      //   state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
      //   state.iStoreInterfaceDataUpdateTime = Date.now()
      // }

      if (data && data.length) {
        state.loginUser.userFunctions.push(...data)

        // Mirror incrementally instead of deep clone
        state.iStoreInterfaceData.push(...data)

        state.iStoreInterfaceDataUpdateTime = Date.now()
      }
    },
    // Update iStoreInterfaceData (removing a single node)
    removeNode(state, data) {
      if (data && state.loginUser?.userFunctions?.length) {
        // state.loginUser.userFunctions = state.loginUser.userFunctions.filter(
        //   item => !(item.parent === data.parent && item.module === data.module && item.name === data.name)
        // )

        // state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
        // state.iStoreInterfaceDataUpdateTime = Date.now()
        state.loginUser.userFunctions = state.loginUser.userFunctions.filter(
          item => !(item.parent === data.parent && item.module === data.module && item.name === data.name)
        )

        state.iStoreInterfaceData = state.iStoreInterfaceData.filter(
          item => !(item.parent === data.parent && item.module === data.module && item.name === data.name)
        )

        state.iStoreInterfaceDataUpdateTime = Date.now()
      }
    },
    // Update iStoreInterfaceData (removing several nodes)
    removeNodes(state, data) {
      // if (data) {
      //   state.loginUser.userFunctions = state.loginUser.userFunctions.filter(
      //     item => !data.some(d => item.parent.includes(d))
      //   )

      //   state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
      //   state.iStoreInterfaceDataUpdateTime = Date.now()
      // }

      if (data && data.length) {
        state.loginUser.userFunctions = state.loginUser.userFunctions.filter(
          item => !data.some(d => item.parent.includes(d)) // adjust if needed
        )

        state.iStoreInterfaceData = state.iStoreInterfaceData.filter(
          item => !data.some(d => item.parent.includes(d))
        )

        state.iStoreInterfaceDataUpdateTime = Date.now()
      }
    },
    setLoginUser(state, loginUser) {
      const cids = Array.isArray(loginUser.cids) ? loginUser.cids : []
      loginUser.cids = cids
      loginUser.cid = cids[0] ?? null
      state.loginUser = loginUser
      state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
      state.iStoreInterfaceDataUpdateTime = Date.now()
    },
    setCompanyIds(state, companyIds) {
      state.loginUser.cid = companyIds
      state.iStoreInterfaceData = JSON.parse(JSON.stringify(state.loginUser.userFunctions))
      state.iStoreInterfaceDataUpdateTime = Date.now()
    },
    setIStoreDatabases(state, databases) {
      state.databases = databases
    },
    setNotificationMsg(state, obj) {
      if (typeof obj === 'object') {
        state.notificationMsg = {
          content: obj.content,
          type: obj.type,
        }
      } else if (typeof obj === 'string') {
        state.notificationMsg = {
          content: obj,
          type: null,
        }
      } else {
        state.notificationMsg = {
          content: null,
          type: null,
        }
      }
      state.notificationMsg.updateTime = Date.now()
    },
    setGlobalSetting(state, data) {
      state.globalSettingData = data
    },
    setWatermarkUrl(state, data) {
      const svgUrl = data ? `url('data:image/svg+xml;base64,${btoa(data)}')` : ""
      state.watermarkSetting.backgroundImage = svgUrl
    },
    resetActivityTimeout(state) {
      state.keepAliveTimestamp = Date.now()
    },
    setLoginTimestamp(state, date) {
      state.loginTimestamp = date
    },
    setFunctions(state, functions) {
      state.menuFunctions = functions;
    },
    showCustomView(state, view) {
      state.customView = view
    },
    showCustomData(state, data) {
      state.customData = data
    },
    updateFolderFunctionDisplayName(state, key) {
      langControl.updateFunctionDisplayName(state.iStoreInterfaceData, key)
      langControl.updateFolderDisplayName(state.menuFunctions, key)
    },
    setLogoutClicked(state, data) {
      state.logoutClicked = data
    },
    toggleItemChecked(state, key) {
      // Toggle individual data table row checkbox
      const itemFound = state.checkedItems.find(item => item.key === key);
      if (itemFound !== undefined) {
        state.checkedItems = state.checkedItems.filter(item => item.key !== key)
      } else {
        state.checkedItems.push({
          key: key,
          checked: true
        })
      }
    },
    togglePageChecked(state, data) {
      // Toggle all checkbox in each data table row by page (set to true)
      data.forEach(d => {
        const itemFound = state.checkedItems.some(dataItem => dataItem.key === d.key);
        console.log(itemFound)
        if (!itemFound) {
          state.checkedItems.push({
            key: d.key,
            checked: true
          })
        }
      })
    },
    clearPageChecked(state, data) {
      // Clear all checkbox in each data table row by page
      data.forEach(d => {
        const itemFound = state.checkedItems.find(item => item.key === d.key);
        if (itemFound !== undefined) {
          state.checkedItems = state.checkedItems.filter(item => item.key !== d.key)
        }
      })
    },
    clearCheckedItems(state) {
      // Clear all checkedItems across all pages
      state.checkedItems = []
    },
    triggerAppMenu(state, isShow) {
      if (typeof isShow === 'boolean') {
        state.isTriggerAppMenu = isShow
      } else {
        state.isTriggerAppMenu = !state.isTriggerAppMenu
      }
    },
    openFolderTemp(state, obj) {
      state.tempOpenFolder = obj
    },
    openFolder(state, obj) {

      let {parentKeys} = obj

      const _func = (folders, key) => {
        for (let m of folders) {
          if (m.key === key) {
            m.opened = true
            break;
          } else if (m.folders && m.folders.length) {
            _func(m.folders, key)
          }
        }
      }

      for (let k of parentKeys) {
        _func(state.menuFunctions, k)
      }
    },
  },
  actions: {
    async initializeWatermark({ commit }, imageUrl) {
      try {
        let base64Image
        let svgUrl
        if (imageUrl) {
          base64Image = await iStoreUtils.getBase64ImageFromUrl(imageUrl)
          svgUrl = `
          <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">
            <image href="${base64Image}" x="0" y="0" width="300" height="300" opacity="0.1" />
          </svg>
        `
        } else {
          svgUrl = ""
        }
        commit('setWatermarkUrl', svgUrl)
      } catch (error) {
        console.error(error)
      }
    },
    initializeActivityTimeout({state, commit, dispatch}) {
      clearInterval(state.activityTimeout)
      state.activityTimeout = setInterval(async () => {
        // Dispatch logout action automatically when the user becomes idle
        if (state.keepAliveTimestamp !== 0 && state.keepAliveTimestamp + iStoreSetting.activityCheckInterval < Date.now()) {
          dispatch('logOut', {byPassConfirm: true})
        }
        // If there is an activity detected in the 4th interval (during the last 15 minutes before token expires) then extend token
        if (state.keepAliveTimestamp > state.loginTimestamp + (60000 * 45)) {
          try {
            const newExpiry = await iStoreApi.extendToken()
            state.loginUser.expiry = newExpiry
            console.debug(`#[Extend token] token expiry extended to: ${state.loginUser.expiry}`)
            commit('setLoginTimestamp', Date.now()) // Renew the login timestamp
          } catch (error) {
            alert(error)
          }
        }
      }, iStoreSetting.activityCheckInterval - 60000) // Subtract current interval setting by 1 minute (as there might be delay in function execution)
    },
    logIn({commit, dispatch}, user) {
      // Set the user in the state
      commit('setLoginUser', user)
      commit('setLoginTimestamp', Date.now())
      dispatch('initializeActivityTimeout') // Initialize activity timeout
    },
    async logOut({state}, {byPassConfirm = false} = {}) {
      try {
        await iStoreApi.logOutAccount(state.loginUser.starNumber)
      } catch (error) {
        alert(error)
      }
      clearInterval(state.activityTimeout)
      iStoreLocalData.setAuthenticated(false)
      iStoreLocalData.removeAll()
      //$router.push({ path: '/' })
      //commit('setLoginTimestamp', 0) // Delete the login timestamp reference in the state
      if (byPassConfirm) {
        state.isLogOutBypassed = true
      } else {
        // window.location.href = '/imanager'
        const port = window.location.port ? `:${window.location.port}` : ''
        window.location.href = `${window.location.protocol}//${window.location.hostname}${port}/imanager/#/login`
      }
      sessionStorage.clear()
    },
  },
  modules: {},
  getters: {
    getWatermarkUrl(state) {
      return state.watermarkSetting.backgroundImage
    }
  },
  plugins: [vuexLocal.plugin]
})
